import { createContext } from 'react';
import type { Array as A } from 'effect';
import type { OcrBoundingPoly } from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageOcrLayer/ocrGeometry';
import type { TwoWayMatchCell } from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageTwoWayMatchLayer/twoWayMatchGeometry';

export type ScreenPoint = { x: number; y: number };

export type OcrTextDragItem = {
  kind: 'ocrText';
  text: string;
  ocrBoundingPoly: OcrBoundingPoly & { page_number: number };
  lineStart: ScreenPoint;
};

export type TwoWayMatchTableDragItem = {
  kind: 'twoWayMatchTable';
  page: number;
  tableCellPolygons: A.NonEmptyReadonlyArray<TwoWayMatchCell>;
  /** The size of the two-way-match layer the cells are drawn on, in screen pixels. */
  layerSize: { width: number; height: number };
};

export type DocumentDragItem = OcrTextDragItem | TwoWayMatchTableDragItem;

const DRAG_ITEM_KINDS: ReadonlyArray<DocumentDragItem['kind']> = ['ocrText', 'twoWayMatchTable'];

export const isDocumentDragItem = (value: unknown): value is DocumentDragItem =>
  typeof value === 'object' &&
  value !== null &&
  'kind' in value &&
  DRAG_ITEM_KINDS.some((kind) => kind === value.kind);

export const DocumentDragItemContext = createContext<DocumentDragItem | null>(null);

export type JournalEntryCellDrop = (item: OcrTextDragItem) => void;
export type JournalEntryCellDrops = { register: (cell: string, drop: JournalEntryCellDrop) => () => void };

// Outside the document page (the Workspace renders the same JE table) nothing is dropped into the cells.
export const JournalEntryCellDropsContext = createContext<JournalEntryCellDrops>({
  register: () => () => undefined,
});

export type TwoWayMatchTableDropZone = { dropTwoWayMatchTable: () => void };

export const isTwoWayMatchTableDropZone = (value: unknown): value is TwoWayMatchTableDropZone =>
  typeof value === 'object' &&
  value !== null &&
  'dropTwoWayMatchTable' in value &&
  typeof value.dropTwoWayMatchTable === 'function';
